<script setup>
import AgeSelectBox from "@/components/common/input/ageSelectBox.vue";
import {ref} from "vue";
import VueDatePicker from "@vuepic/vue-datepicker";
import '@vuepic/vue-datepicker/dist/main.css';
import MeetingStatus from "@/components/common/input/meetingType.vue";
import CommitteeSelectBox from "@/components/common/input/committeeSelectBox.vue";
import MeetingRoomSelectBox from "@/components/common/input/meetingRoomSelectBox.vue";
import {useLanguageStore, useLoadingStore} from "@/pinia";
import {useToast} from "@/composables/useToast";

const toast = useToast();
const loadingStore = useLoadingStore();
const languageStore = useLanguageStore();

// Form ref
const formRef = ref(null);
const isFormValid = ref(false);

const meetingPreDt = ref("");
const ageId = ref("");
const sessionId = ref("");
const committee = ref("");
const meetingType = ref();
const meetingLocation = ref("");
const memo = ref("");
const stepId = ref();
const isPrivate = ref(false);
const isYoutube = ref(false);
const today = new Date();

// Validation rules
const rules = {
  required: v => !!v || languageStore.get('requiredField')
};

const set_ageId = (sAgeId) => {
  ageId.value = sAgeId;
}

const props = defineProps({
  isInsertMeeting: Boolean,
})

const emits = defineEmits([
  'set_isInsertMeeting', 'fn_saveMeeting'
])

const close = () => {
  meetingPreDt.value = "";
  ageId.value = "";
  sessionId.value = "";
  committee.value = "";
  meetingType.value = "";
  meetingLocation.value = "";
  memo.value = "";
  isPrivate.value = false;
  isYoutube.value = false;
  stepId.value = "";
  formRef.value?.reset();
  emits('set_isInsertMeeting', false);
}

const save = async () => {
  // v-form validation
  const {valid} = await formRef.value.validate();

  // Custom validation for non-Vuetify components
  let hasCustomError = false;
  if (!ageId.value) {
    toast.warning(languageStore.get('pleaseSelectSession'));
    hasCustomError = true;
  } else if (!meetingPreDt.value) {
    toast.warning(languageStore.get('pleaseSelectScheduledMeetingDate'));
    hasCustomError = true;
  } else if (!meetingType.value) {
    toast.warning(languageStore.get('pleaseSelectMeetingType'));
    hasCustomError = true;
  }

  if (hasCustomError || !valid) {
    if (!hasCustomError && !valid) {
      toast.warning(languageStore.get('pleaseCheckInputFields'));
    }
    return;
  }

  let data;
  if (meetingType.value == "mt002") {
    data = {
      "age": {"ageId": ageId.value},
      "sessionId": sessionId.value,
      "meetingType": {"codeId": meetingType.value},
      "meetingLocation": {"codeId": meetingLocation.value},
      "memo": memo.value,
      "meetingPreDt": meetingPreDt.value,
      "isPrivate": isPrivate.value,
      "isYoutube": isYoutube.value,
    }
  } else {
    data = {
      "age": {"ageId": ageId.value},
      "sessionId": sessionId.value,
      "committeeId": {"codeId": committee.value},
      "meetingType": {"codeId": meetingType.value},
      "meetingLocation": {"codeId": meetingLocation.value},
      "memo": memo.value,
      "meetingPreDt": meetingPreDt.value,
      "isPrivate": isPrivate.value,
      "isYoutube": isYoutube.value
    }
  }
  emits("fn_saveMeeting", data);
  close();
}

const set_meetingType = (s_meetingType) => {
  meetingType.value = s_meetingType;
}

const set_committee = (s_committee) => {
  committee.value = s_committee;
}

const set_meetingLocation = (s_meetingLocation) => {
  meetingLocation.value = s_meetingLocation;
}
</script>

<template>
  <v-dialog
      :model-value="isInsertMeeting"
      @update:model-value="close"
      width="600"
  >
    <v-card>
      <v-card-title class="modal-header">
        {{ languageStore.get('meetingRegistration') }}
      </v-card-title>

      <v-card-text class="modal-body">
        <v-form ref="formRef" v-model="isFormValid">
          <!-- 회기 / 회차 / Step -->
          <v-row>
            <v-col cols="6">
              <age-select-box
                  @set_ageId="set_ageId"
                  :label="languageStore.get('session') + ' *'"
              />
            </v-col>
            <v-col cols="3">
              <v-text-field
                  v-model="sessionId"
                  :label="languageStore.get('meetingRound')"
                  variant="outlined"
                  density="compact"
                  hide-details="auto"
              />
            </v-col>
            <v-col cols="3">
              <v-text-field
                  v-model="stepId"
                  :label="languageStore.get('step')"
                  variant="outlined"
                  density="compact"
                  hide-details="auto"
              />
            </v-col>
          </v-row>

          <!-- 회의 예정일 -->
          <vue-date-picker
              v-model="meetingPreDt"
              locale="ko"
              :min-date="today"
              :is-24="false"
              :placeholder="languageStore.get('scheduledMeetingDate') + ' *'"
              class="dp-full-width mb-4 mt-4"
              :teleport="true"
          />

          <!-- 회의 유형 / 위원회 -->
          <v-row>
            <v-col cols="5">
              <meeting-status
                  :meeting="meetingType"
                  @set_meetingType="set_meetingType"
                  :label="languageStore.get('meetingType') + ' *'"
              />
            </v-col>
            <v-col cols="7" v-if="meetingType === 'mt001'">
              <committee-select-box
                  @set_committee="set_committee"
                  :label="languageStore.get('committee')"
              />
            </v-col>
          </v-row>

          <!-- 회의실 -->
          <meeting-room-select-box
              @set_meetingRoom="set_meetingLocation"
              :label="languageStore.get('meetingRoom')"
              class="mb-4"
          />

          <!-- 메모 -->
          <v-textarea
              v-model="memo"
              :label="languageStore.get('memo')"
              variant="outlined"
              density="compact"
              rows="3"
              hide-details="auto"
              class="mb-4"
          />

          <!-- 스위치 옵션 -->
          <v-row>
            <v-col cols="6">
              <v-switch
                  v-model="isPrivate"
                  :label="languageStore.get('private')"
                  color="indigo"
                  hide-details
                  density="compact"
              />
            </v-col>
            <v-col cols="6">
              <v-switch
                  v-model="isYoutube"
                  color="red"
                  hide-details
                  density="compact"
              >
                <template #label>
                  <v-icon color="red" size="20" class="mr-1">mdi-youtube</v-icon>
                  <span>YouTube</span>
                </template>
              </v-switch>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-card-actions class="modal-footer">
        <v-progress-circular
            v-if="loadingStore.isLoading"
            color="primary"
            indeterminate
            size="24"
        />
        <v-spacer />
        <v-btn
            v-if="!loadingStore.isLoading"
            color="primary"
            variant="flat"
            @click="save"
        >
          {{ languageStore.get('save') }}
        </v-btn>
        <v-btn
            variant="outlined"
            @click="close"
        >
          {{ languageStore.get('close') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>
/* 모달 헤더 */
.modal-header {
  background-color: #1A3A5C !important;
  color: #FFFFFF !important;
  padding: 16px 24px;
  font-weight: 600;
  font-size: 18px;
}

/* 모달 바디 - Vuetify 기본 padding 제거 */
:deep(.v-card-text.modal-body) {
  padding: 24px !important;
}

/* 모달 바디 내 입력 필드 간격 */
.modal-body :deep(.v-input),
.modal-body :deep(.dp-full-width) {
  margin-bottom: 16px;
}

/* v-row 내부의 v-input은 margin 제외 */
.modal-body :deep(.v-row .v-input),
.modal-body :deep(.v-row .dp-full-width) {
  margin-bottom: 0;
}

.modal-body :deep(.v-row) {
  margin-top: 0;
  margin-bottom: 16px;
}

.modal-body :deep(.v-col) {
  padding-top: 0;
  padding-bottom: 0;
}

.modal-body :deep(.v-row:last-child) {
  margin-bottom: 0;
}

/* 모달 푸터 - Vuetify 기본 padding 제거 */
:deep(.v-card-actions.modal-footer) {
  padding: 16px 24px !important;
  border-top: 1px solid #DEE2E6;
  gap: 8px;
}

/* DatePicker 전체 너비 */
.dp-full-width {
  width: 100%;
}

.dp-full-width :deep(.dp__input) {
  height: 40px;
  border-radius: 4px;
  border-color: #C7C7C7;
}

.dp-full-width :deep(.dp__input:hover) {
  border-color: #1A3A5C;
}
</style>
